import React from 'react';
import { motion } from 'framer-motion';

const STORY_TEXT_VERTICAL_OFFSET_PX = 56;

export default function StoryletOverlay({
  open,
  cardState,
  activeStorylet,
  isMobile,
  swipeDirection,
  storyDragX,
  setStoryDragX,
  isStoryStepFlipping,
  storyNextBg,
  getActiveStep,
  handleStorySwipe,
  advanceStoryTap,
  tileWidth,
  tileHeight,
  fallbackImage,
}) {
  if (!open || cardState !== 'story' || !activeStorylet) return null;

  const storyDragDir =
    storyDragX > 25 ? 'right' : storyDragX < -25 ? 'left' : null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 1000 }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <motion.div
        className="relative"
        style={{
          width: tileWidth * 5,
          height: tileHeight * 5,
          perspective: '1000px',
        }}
      >
        {(() => {
          const step = getActiveStep();
          if (!step) return null;

          const isChoice = step.type === 'choice';
          const leftLabel = isChoice ? step.left?.label || 'Left' : '';
          const rightLabel = isChoice ? step.right?.label || 'Right' : '';

          const bg = step.image || activeStorylet.coverImage || fallbackImage;
          const panelText = String(
            step.type === 'page' ? step.text || '' : step.prompt || ''
          ).trim();
          const showTextPanel = step.textEnabled !== false && panelText.length > 0;

          return (
            <>
              {isChoice && !swipeDirection && (
                isMobile ? (
                  <div
                    className="fixed left-0 right-0 z-[1005] px-3 flex justify-between gap-3 pointer-events-none"
                    style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) - 36px)' }}
                  >
                    <div
                      className={`flex items-center bg-black/60 px-4 py-2.5 rounded-2xl border shadow-lg text-white font-karma opacity-90 leading-tight text-base transition-transform duration-75 ${
                        storyDragDir === 'left'
                          ? 'border-amber-300/70 bg-amber-500/30 scale-[1.03]'
                          : 'border-white/10'
                      }`}
                      style={{ whiteSpace: 'pre-line' }}
                    >
                      <span className="mr-2 text-xl leading-none">←</span>
                      {leftLabel}
                    </div>

                    <div
                      className={`flex items-center bg-black/60 px-4 py-2.5 rounded-2xl border shadow-lg text-white font-karma opacity-90 leading-tight text-base whitespace-nowrap transition-transform duration-75 ${
                        storyDragDir === 'right'
                          ? 'border-amber-300/70 bg-amber-500/30 scale-[1.03]'
                          : 'border-white/10'
                      }`}
                    >
                      {rightLabel}
                      <span className="ml-2 text-xl leading-none">→</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      className="absolute z-[5] text-white font-karma opacity-75 leading-tight md:text-xl lg:text-2xl"
                      style={{
                        left: -240,
                        top: '45%',
                        transform: 'translateY(-50%)',
                        whiteSpace: 'pre-line',
                        textAlign: 'right',
                      }}
                    >
                      {leftLabel}
                    </div>
                    <div
                      className="absolute z-[5] text-white font-karma opacity-75 leading-tight md:text-xl lg:text-2xl"
                      style={{
                        right: -220,
                        top: '45%',
                        transform: 'translateY(-50%)',
                        whiteSpace: 'pre-line',
                        textAlign: 'left',
                      }}
                    >
                      {rightLabel}
                    </div>
                  </>
                )
              )}

              <motion.div
                className="absolute inset-0"
                initial={{ y: '100vh', opacity: 0.0, scale: 0.98 }}
                animate={{
                  y: 0,
                  opacity: 1,
                  scale: 1,
                  x: swipeDirection ? (swipeDirection === 'left' ? -420 : 420) : 0,
                  rotate: swipeDirection ? (swipeDirection === 'left' ? -8 : 8) : 0,
                }}
                transition={{
                  duration: 0.35,
                  type: 'spring',
                  stiffness: 110,
                  damping: 14,
                }}
                drag={isChoice ? 'x' : false}
                dragConstraints={isChoice ? { left: -140, right: 140 } : undefined}
                dragElastic={isChoice ? 0.12 : 0}
                dragMomentum={false}
                dragSnapToOrigin
                onDrag={(event, info) => {
                  if (isChoice && !swipeDirection) setStoryDragX(info.offset.x);
                }}
                onDragEnd={(event, info) => {
                  setStoryDragX(0);
                  if (!isChoice) return;

                  if (Math.abs(info.offset.x) > 50 && !swipeDirection) {
                    handleStorySwipe(info.offset.x > 0 ? 'right' : 'left');
                  }
                }}
                style={{
                  touchAction: 'pan-y',
                  cursor: isChoice ? 'grab' : 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isChoice) advanceStoryTap();
                }}
              >
                <div className="absolute inset-0 border border-black rounded-[14px] overflow-hidden">
                  <motion.div
                    className="absolute inset-0 bg-center backface-hidden"
                    style={{
                      backgroundImage: `url(${bg})`,
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      zIndex: 2,
                      pointerEvents: 'none',
                      borderRadius: 14,
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                    }}
                    animate={{ rotateY: isStoryStepFlipping ? 180 : 0 }}
                    transition={{ duration: 0.5 }}
                  />

                  <motion.div
                    className="absolute inset-0 bg-center backface-hidden"
                    style={{
                      backgroundImage: `url(${storyNextBg || bg})`,
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      transform: 'rotateY(180deg)',
                      zIndex: 1,
                      pointerEvents: 'none',
                      borderRadius: 14,
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                    }}
                    animate={{ rotateY: isStoryStepFlipping ? 0 : -180 }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </motion.div>

              {showTextPanel && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 z-[6] w-[85%]"
                  style={{ bottom: -STORY_TEXT_VERTICAL_OFFSET_PX }}
                >
                  <div className="bg-black/70 border border-white/10 rounded-xl px-4 py-3 text-white font-karma text-base leading-snug text-center">
                    {panelText}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </motion.div>
    </div>
  );
}